import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["content", "contentRight", "description", "descriptionRight", "disabled", "enlargeOnSelect", "focus", "height", "hovered", "id", "isChecked", "isFirst", "isFlat", "isLast", "isNextBoxChecked", "isPreviousBoxChecked", "isShowingDescriptionOnSelect", "isVertical", "keyboardFocused", "left", "name", "onChange", "onDisabledClick", "onLayout", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPress", "preview", "pressed", "singleRow", "size", "shouldWordsBreak", "testID", "UNSAFE_inputProps", "value"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import Text from '@bubble-ui/primitive-text';
import reformatObfuscation from '@bubble-ui/util-reformat-obfuscation';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import { useDesignContext } from '@klarna/designtokens';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { BoxSelectorOptionBase } from '../BoxSelectorOptionBase';
import { defaultDesignTokens } from '../defaultDesignTokens';
var getDefaultStyleSheet = _ref => {
  var {
    description,
    designContext,
    disabled,
    hovered,
    pressed,
    singleRow,
    contentRight,
    descriptionRight,
    isChecked,
    isShowingDescriptionOnSelect,
    left
  } = _ref;
  var titleStyle = _objectSpread(_objectSpread({
    color: designContext.get('box-selector/title-text-color', {
      interactiveState: disabled ? 'disabled' : pressed ? 'pressed' : hovered ? 'hovered' : 'default'
    }).toString()
  }, designContext.get('box-selector/title-text-style').styles()), {}, {
    transition: 'color 0.2s ease'
  });
  var descriptionStyle = _objectSpread({
    color: designContext.get('box-selector/description-text-color', {
      interactiveState: disabled ? 'disabled' : pressed ? 'pressed' : hovered ? 'hovered' : 'default'
    }).toString(),
    pointerEvents: 'none',
    transition: 'color 0.2s ease'
  }, designContext.get('box-selector/description-text-style', {
    isSingleRow: singleRow ? 'singleRow' : 'default'
  }).styles());
  var rightStyle = {
    textAlign: 'right',
    flexGrow: 1
  };
  return {
    titleWrapperStyle: _objectSpread(_objectSpread({
      flex: singleRow ? 1 : 0,
      flexBasis: 'auto'
    }, contentRight ? {
      display: 'flex'
    } : {}), {}, {
      marginTop: Boolean(description) && !singleRow ? -2 : 0
    }),
    descriptionWrapperStyle: _objectSpread({
      justifyContent: 'space-between'
    }, Boolean(isShowingDescriptionOnSelect) && !isChecked ? {
      display: 'none'
    } : descriptionRight ? {
      display: 'flex',
      flexDirection: 'row',
      alignSelf: 'stretch'
    } : {}),
    titleLeftStyle: _objectSpread(_objectSpread({
      flex: 0,
      marginRight: left ? 10 : 0
    }, titleStyle), contentRight ? {
      flexBasis: 'auto'
    } : {}),
    descriptionLeftStyle: _objectSpread(_objectSpread({}, descriptionStyle), singleRow ? rightStyle : {}),
    previewStyle: {
      color: designContext.get('box-selector/description-text-color').toString()
    },
    titleRightStyle: _objectSpread({}, contentRight ? _objectSpread(_objectSpread({}, titleStyle), rightStyle) : {
      display: 'none'
    }),
    descriptionRightStyle: _objectSpread({}, descriptionRight ? _objectSpread(_objectSpread({}, descriptionStyle), rightStyle) : {
      display: 'none'
    })
  };
};
function BoxSelectorDetailedOption(_ref2) {
  var {
      content,
      contentRight,
      description,
      descriptionRight,
      disabled,
      enlargeOnSelect,
      focus,
      height,
      hovered,
      id,
      isChecked,
      isFirst,
      isFlat,
      isLast,
      isNextBoxChecked,
      isPreviousBoxChecked,
      isShowingDescriptionOnSelect,
      isVertical,
      keyboardFocused,
      left,
      name,
      onChange,
      onDisabledClick,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onLayout,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPress,
      preview,
      pressed,
      singleRow,
      size,
      shouldWordsBreak,
      testID,
      UNSAFE_inputProps,
      value
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  usePropsSpreadingDeprecationWarning({
    componentName: 'BoxSelectorDetailed.Option',
    props,
    useInstead: 'UNSAFE_inputProps',
    removalDate: 'v7.0.0'
  });
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombined({
    disabled,
    hovered,
    keyboardFocused,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    pressed
  });
  var designContext = useDesignContext(defaultDesignTokens);
  var designVersion = useDesignVersion();
  var {
    titleWrapperStyle,
    descriptionWrapperStyle,
    titleLeftStyle,
    descriptionLeftStyle,
    titleRightStyle,
    descriptionRightStyle,
    previewStyle
    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
  } = useStyleSheet(BoxSelectorDetailedOption, getDefaultStyleSheet, _objectSpread(_objectSpread({}, props), {}, {
    content,
    contentRight,
    description,
    descriptionRight,
    designContext,
    designVersion,
    disabled: interactiveStateCombined.disabled,
    enlargeOnSelect,
    height,
    focus,
    hovered: interactiveStateCombined.hovered,
    id,
    isChecked,
    isFirst,
    isFlat,
    isLast,
    isNextBoxChecked,
    isPreviousBoxChecked,
    isShowingDescriptionOnSelect,
    isVertical,
    keyboardFocused: interactiveStateCombined.keyboardFocused,
    left,
    name,
    onChange,
    onDisabledClick,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPress,
    pressed: interactiveStateCombined.pressed,
    preview,
    singleRow,
    size,
    shouldWordsBreak,
    testID,
    UNSAFE_inputProps,
    value
  }));
  return /*#__PURE__*/React.createElement(BoxSelectorOptionBase, _extends({
    enlargeOnSelect: enlargeOnSelect,
    focus: focus,
    height: height,
    id: id,
    isFirst: isFirst,
    isFlat: isFlat,
    isLast: isLast,
    isNextBoxChecked: isNextBoxChecked,
    isPreviousBoxChecked: isPreviousBoxChecked,
    isVertical: isVertical,
    name: name,
    onChange: onChange,
    onDisabledClick: onDisabledClick,
    onPress: onPress,
    size: size,
    shouldWordsBreak: shouldWordsBreak,
    testID: testID,
    UNSAFE_inputProps: UNSAFE_inputProps,
    value: value
  }, props, {
    disabled: interactiveStateCombined.disabled,
    hovered: interactiveStateCombined.hovered,
    keyboardFocused: interactiveStateCombined.keyboardFocused,
    pressed: interactiveStateCombined.pressed,
    isChecked: isChecked,
    isShowingDescriptionOnSelect: isShowingDescriptionOnSelect,
    left: left,
    singleRow: singleRow
  }, eventHandlers), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: titleWrapperStyle
  }, /*#__PURE__*/React.createElement(Text, {
    style: titleLeftStyle
  }, content, preview && /*#__PURE__*/React.createElement(Text, {
    style: previewStyle
  }, reformatObfuscation("\xA0".concat(preview)))), /*#__PURE__*/React.createElement(Text, {
    style: titleRightStyle
  }, contentRight)), /*#__PURE__*/React.createElement("div", {
    style: descriptionWrapperStyle
  }, /*#__PURE__*/React.createElement(Text, {
    style: descriptionLeftStyle
  }, description), /*#__PURE__*/React.createElement(Text, {
    style: descriptionRightStyle
  }, descriptionRight))));
}
BoxSelectorDetailedOption.displayName = 'BoxSelectorDetailedOption';
export var Option = withProvisionalOverrideFromContext(BoxSelectorDetailedOption);
//# sourceMappingURL=index.js.map